import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { api } from "../api";
import { useI18n, type Lang } from "../lib/i18n";
import { errorCode } from "../lib/plan";

/**
 * The "set your password" screen an approval invite links to.
 *
 * Public: it renders before the console asks who is signed in (see main.tsx),
 * because the person following the link has no session and no password yet.
 * It deliberately does not sign anyone in — the server never sets a cookie
 * here — so the last step is a link to the ordinary sign-in, which is what
 * walks them through enrolling the second factor.
 */

const MIN = 12;
const MAX = 200;

export function SetPasswordView({ token, onLang }: { token: string; onLang: (lang: Lang) => void }): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.setPassword;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const longEnough = password.length >= MIN && password.length <= MAX;
  const matches = password === confirm;

  function submit(event: Event): void {
    event.preventDefault();
    if (!longEnough) {
      setError(t.weak);
      return;
    }
    if (!matches) {
      setError(t.mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    void api
      .setPassword(token, password)
      .then(() => {
        // The link is spent; drop it from the address bar and from the back
        // stack so a shared screen or a borrowed browser cannot show it.
        // replaceState, not a hash change: this must not be a history entry.
        history.replaceState(null, "", "#/set-password");
        setDone(true);
      })
      .catch((err: unknown) => {
        const code = errorCode(err);
        setError(code === "weak_password" ? t.weak : code === "invalid_token" ? t.invalid : t.failed);
      })
      .finally(() => setBusy(false));
  }

  return (
    <div class="auth">
      <div class="card">
        <div class="lang-switch" role="group" aria-label={all.login.language}>
          {(["tr", "en"] as const).map((l) => (
            <button key={l} aria-pressed={lang === l} onClick={() => onLang(l)}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {done ? (
          <div>
            <h1>{t.doneTitle}</h1>
            <p class="sub">{t.doneBody}</p>
            {/* A hash change, not a reload: the shell simply renders the sign-in screen. */}
            <a class="btn primary" href="#/inbox">
              {t.goLogin}
            </a>
          </div>
        ) : !token ? (
          <div>
            <h1>{t.title}</h1>
            <div class="alert error">{t.missingToken}</div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1>{t.title}</h1>
            <p class="sub">{t.intro}</p>

            <label for="new-password">{t.password}</label>
            <input
              id="new-password"
              type="password"
              autocomplete="new-password"
              minLength={MIN}
              maxLength={MAX}
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
              required
              autofocus
            />
            <p class="sub">{t.rule}</p>

            <label for="confirm-password">{t.confirm}</label>
            <input
              id="confirm-password"
              type="password"
              autocomplete="new-password"
              maxLength={MAX}
              value={confirm}
              onInput={(e) => setConfirm(e.currentTarget.value)}
              required
            />

            <button class="btn primary" disabled={busy || !longEnough || !matches}>
              {busy ? t.saving : t.submit}
            </button>
          </form>
        )}

        {error ? <div class="alert error">{error}</div> : null}
      </div>
    </div>
  );
}
